import { z } from 'zod'

import { fuzzyNumberToNumber } from './schema'

export const MAX_QUEUE_BATCH_SIZE = 1000

export const SCALE_BINARY = 'binary'
export const SCALE_3_POINT = '3-point'
export const SCALE_4_POINT = '4-point'
export const SCALE_5_POINT = '5-point'
export const SCORECARD_SCALES = [
  SCALE_BINARY,
  SCALE_3_POINT,
  SCALE_4_POINT,
  SCALE_5_POINT,
] as const

export const ScorecardScales = z.enum(SCORECARD_SCALES)
export const CriteriaWeightRange = z.number().min(0).max(20)

export const ScorecardContextFilter = z.object({
  field: z.enum(['channelId', 'tagIds', 'method']),
  op: z.enum(['in', 'nin']),
  values: z.array(z.string().min(1)).min(1),
})

export const SCALE_BINARY_MET_ICON = '👍'
export const SCALE_BINARY_NOT_MET_ICON = '👎'

export const SCALE_3_POINT_0_ICON = '🙁'
export const SCALE_3_POINT_1_ICON = '😐'
export const SCALE_3_POINT_2_ICON = '😄'

export const SCALE_4_POINT__2_ICON = '😠'
export const SCALE_4_POINT__1_ICON = '😕'
export const SCALE_4_POINT_1_ICON = '😄'
export const SCALE_4_POINT_2_ICON = '🤩'

export const SCALE_5_POINT_0_ICON = '😠'
export const SCALE_5_POINT_1_ICON = '😕'
export const SCALE_5_POINT_2_ICON = '😐'
export const SCALE_5_POINT_3_ICON = '😄'
export const SCALE_5_POINT_4_ICON = '🤩'

export const SCALE_5_POINT_0_LABEL = 'Not Met'
export const SCALE_5_POINT_1_LABEL = 'Needs Improvement'
export const SCALE_5_POINT_2_LABEL = 'Approaches Expectations'
export const SCALE_5_POINT_3_LABEL = 'Meets Expectations'
export const SCALE_5_POINT_4_LABEL = 'Exceeds Expectations'

export const SCALE_5_POINT_NM_POINTS = 0
export const SCALE_5_POINT_NI_POINTS = 1
export const SCALE_5_POINT_AE_POINTS = 2
export const SCALE_5_POINT_ME_POINTS = 3
export const SCALE_5_POINT_EE_POINTS = 4

export const SCALE_4_POINT__2_LABEL = 'Not Met'
export const SCALE_4_POINT__1_LABEL = 'Needs Improvement'
export const SCALE_4_POINT_1_LABEL = 'Meets Expectations'
export const SCALE_4_POINT_2_LABEL = 'Exceeds Expectations'

export const SCALE_4_POINT_NM_POINTS = 0
export const SCALE_4_POINT_NI_POINTS = 1
export const SCALE_4_POINT_ME_POINTS = 2
export const SCALE_4_POINT_EE_POINTS = 3

export const SCALE_3_POINT_0_LABEL = 'Not Met'
export const SCALE_3_POINT_1_LABEL = 'Partially Met'
export const SCALE_3_POINT_2_LABEL = 'Met'

export const SCALE_3_POINT_NM_POINTS = 0
export const SCALE_3_POINT_PM_POINTS = 1
export const SCALE_3_POINT_ME_POINTS = 2

export const SCALE_BINARY_MET_LABEL = 'Met'
export const SCALE_BINARY_NOT_MET_LABEL = 'Not Met'

export const SCALE_BINARY_NM_POINTS = 0
export const SCALE_BINARY_ME_POINTS = 1

export const SCALE_SCORE_MAPPING = {
  [SCALE_BINARY]: {
    [SCALE_BINARY_NM_POINTS]: 0,
    [SCALE_BINARY_ME_POINTS]: 1,
  },
  [SCALE_3_POINT]: {
    [SCALE_3_POINT_NM_POINTS]: 0,
    [SCALE_3_POINT_PM_POINTS]: 0.8,
    [SCALE_3_POINT_ME_POINTS]: 1,
  },
  [SCALE_4_POINT]: {
    [SCALE_4_POINT_NM_POINTS]: 0,
    [SCALE_4_POINT_NI_POINTS]: 0.6,
    [SCALE_4_POINT_ME_POINTS]: 0.9,
    [SCALE_4_POINT_EE_POINTS]: 1,
  },
  [SCALE_5_POINT]: {
    [SCALE_5_POINT_NM_POINTS]: 0,
    [SCALE_5_POINT_NI_POINTS]: 0.6,
    [SCALE_5_POINT_AE_POINTS]: 0.75,
    [SCALE_5_POINT_ME_POINTS]: 0.9,
    [SCALE_5_POINT_EE_POINTS]: 1,
  },
} satisfies Record<ScorecardScale, Record<number, number>>

export type ScorecardScales = typeof SCORECARD_SCALES
export type ScorecardScale = (typeof SCORECARD_SCALES)[number]

export const DEAD_AIR_THRESHOLD_PHONE = 10
export const DEAD_AIR_THRESHOLD_CHAT = 60
export const DEAD_AIR_THRESHOLD_EMAIL = 300

export const SPEED_OF_ANSWER_THRESHOLD = 28
export const AVERAGE_HANDLE_TIME_THRESHOLD = 300
export const ABANDON_RATE_THRESHOLD = 10
export const FIRST_RESPONSE_TIME_THRESHOLD = 40
export const SCORECARD_SCORE_THRESHOLD = 0.85

export const DEFAULT_THRESHOLD = {
  deadAir: {
    phone: DEAD_AIR_THRESHOLD_PHONE,
    chat: DEAD_AIR_THRESHOLD_CHAT,
    email: DEAD_AIR_THRESHOLD_EMAIL,
  },
  phone: {
    speedOfAnswer: SPEED_OF_ANSWER_THRESHOLD,
    handleTime: AVERAGE_HANDLE_TIME_THRESHOLD,
    abandonRate: ABANDON_RATE_THRESHOLD,
    deadAir: DEAD_AIR_THRESHOLD_PHONE,
  },
  chat: {
    firstResponseTime: FIRST_RESPONSE_TIME_THRESHOLD,
  },
  scorecard: {
    score: SCORECARD_SCORE_THRESHOLD,
  },
}

export const thresholdSchema = z
  .object({
    chat: z
      .object({
        firstResponseTime: fuzzyNumberToNumber,
      })
      .partial(),
    phone: z
      .object({
        speedOfAnswer: fuzzyNumberToNumber,
        handleTime: fuzzyNumberToNumber,
        abandonRate: fuzzyNumberToNumber,
        deadAir: fuzzyNumberToNumber,
      })
      .partial(),
    scorecard: z
      .object({
        score: fuzzyNumberToNumber,
      })
      .partial(),
  })
  .partial()

export type ScorecardThresholds = z.infer<typeof thresholdSchema>

export const scorecardScaleOptions = {
  [SCALE_BINARY]: [
    { value: 1, label: SCALE_BINARY_MET_ICON },
    { value: 0, label: SCALE_BINARY_NOT_MET_ICON },
  ],
  [SCALE_3_POINT]: [
    { value: 2, label: SCALE_3_POINT_2_ICON },
    { value: 1, label: SCALE_3_POINT_1_ICON },
    { value: 0, label: SCALE_3_POINT_0_ICON },
  ],
  [SCALE_4_POINT]: [
    { value: 2, label: SCALE_4_POINT_2_ICON },
    { value: 1, label: SCALE_4_POINT_1_ICON },
    { value: -1, label: SCALE_4_POINT__1_ICON },
    { value: -2, label: SCALE_4_POINT__2_ICON },
  ],
  [SCALE_5_POINT]: [
    { value: 4, label: SCALE_5_POINT_4_ICON },
    { value: 3, label: SCALE_5_POINT_3_ICON },
    { value: 2, label: SCALE_5_POINT_2_ICON },
    { value: 1, label: SCALE_5_POINT_1_ICON },
    { value: 0, label: SCALE_5_POINT_0_ICON },
  ],
}

export const versionedScaleOptions = {
  null: scorecardScaleOptions,
  20250231: {
    [SCALE_BINARY]: [
      { value: SCALE_BINARY_ME_POINTS, label: SCALE_BINARY_MET_ICON },
      { value: SCALE_BINARY_NM_POINTS, label: SCALE_BINARY_NOT_MET_ICON },
    ],
    [SCALE_3_POINT]: [
      { value: SCALE_3_POINT_ME_POINTS, label: SCALE_3_POINT_2_ICON },
      { value: SCALE_3_POINT_PM_POINTS, label: SCALE_3_POINT_1_ICON },
      { value: SCALE_3_POINT_NM_POINTS, label: SCALE_3_POINT_0_ICON },
    ],
    [SCALE_4_POINT]: [
      { value: SCALE_4_POINT_EE_POINTS, label: SCALE_4_POINT_2_ICON },
      { value: SCALE_4_POINT_ME_POINTS, label: SCALE_4_POINT_1_ICON },
      { value: SCALE_4_POINT_NI_POINTS, label: SCALE_4_POINT__1_ICON },
      { value: SCALE_4_POINT_NM_POINTS, label: SCALE_4_POINT__2_ICON },
    ],
    [SCALE_5_POINT]: [
      { value: SCALE_5_POINT_EE_POINTS, label: SCALE_5_POINT_4_ICON },
      { value: SCALE_5_POINT_ME_POINTS, label: SCALE_5_POINT_3_ICON },
      { value: SCALE_5_POINT_AE_POINTS, label: SCALE_5_POINT_2_ICON },
      { value: SCALE_5_POINT_NI_POINTS, label: SCALE_5_POINT_1_ICON },
      { value: SCALE_5_POINT_NM_POINTS, label: SCALE_5_POINT_0_ICON },
    ],
  },
}
